import {Component, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import {NgSelectConfig, NgSelectModule} from "@ng-select/ng-select";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup, FormsModule, ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {Observable, of, Subject} from "rxjs";
import {catchError, debounceTime, distinctUntilChanged, filter, map, switchMap} from "rxjs/operators";
import {SwalComponent} from "@sweetalert2/ngx-sweetalert2";
import {RemedialImagesComponent} from "../../shared/remedial-images/remedial-images.component";
import {BaseComponent} from "../../shared/base-component";
import {Customer} from "../../_models/Customer";
import {Quote} from "../../_models/Quote";
import {Remedial} from "../../_models/Remedial";
import {RemedialRow} from "../../_models/RemedialRow";
import {HelpDet} from "../../_models/HelpDet";
import {RemedialService} from "../../_services/remedial.service";
import {QuoteService} from "../../_services/quote.service";
import {CustomerService} from "../../_services/customer.service";
import {ToastrService} from "ngx-toastr";
import {QuoteSearchCriteria} from "../../_models/QuoteSearchCriteria";
import {NgxDropzoneModule} from "ngx-dropzone";
import {LabeledInputComponent} from "../../shared/labeled-input/labeled-input.component";
import {AsyncPipe, CommonModule} from "@angular/common";
import {SweetAlert2Module} from '@sweetalert2/ngx-sweetalert2';
import {NgbDatepickerModule, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';


@Component({
  imports: [
    NgxDropzoneModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    LabeledInputComponent,
    AsyncPipe,
    RemedialImagesComponent,
    SweetAlert2Module,
    CommonModule,
    NgbDatepickerModule
  ],
  templateUrl: './create-remedial.component.html',
  encapsulation: ViewEncapsulation.None
})
export class CreateRemedialComponent extends BaseComponent implements OnInit {
  @ViewChild(RemedialImagesComponent) remedialImages: RemedialImagesComponent;
  @ViewChild('createTraceRequestSwal') createTraceRequestSwal: SwalComponent;

  remedialForm = this.formBuilder.group({
    name: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
    description: new FormControl(null, [Validators.required]),
    serialNumber: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
    estimatedCost: new FormControl(null, [Validators.min(0)]),
    quoteId: new FormControl(null),
    park: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
    plot: new FormControl(null, [Validators.required, Validators.maxLength(128)]),
    installationDate: new FormControl(null),
    chargeable: new FormControl(false),
    chargeableCost: new FormControl(null, [Validators.min(0)]),
    contractNumber: new FormControl(null),
    traceRequest: new FormControl(false)
  })
  files: File[] = [];
  fileContents: string[] = [];
  showCamera: boolean = false;
  showDocUpload: boolean = false;

  passedContractNumber: number;

  selectedCustomer: Customer;
  quotes: Observable<Quote[]>;
  customers: Customer[];
  quoteSearchInput = new Subject<string | null>();
  customerSearchInput = new Subject<string | null>();
  passedCustomerId: string;
  passedCustomerName: string;
  public remedial: Remedial;
  emptyRemedialRows: RemedialRow[] = [];
  installationDateStruct: NgbDateStruct;
  public quoteId: string;
  selectedQuote: Quote | null = null;

  miFlagged: boolean;

  rem: Remedial;

  remedialItems: HelpDet[] = [];
  remedialLocations: HelpDet[] = [];

  constructor(private remedialService: RemedialService, private quoteService: QuoteService,
              private ngSelectConfig: NgSelectConfig, private formBuilder: FormBuilder, private customerService: CustomerService,
              private router: Router, private route: ActivatedRoute, protected toasterService: ToastrService) {
    super(toasterService);
    this.ngSelectConfig.bindValue = 'id';
  }

  ngOnInit() {
    this.quoteId = this.route.snapshot.paramMap.get('id');

    this.getAllRemedialItems();
    this.getAllRemedialLocations();

    const remParam = this.route.snapshot.queryParamMap.get('remData');
    if (remParam) {
      this.rem = JSON.parse(remParam);
    }

    this.route.queryParams.subscribe(params => {
      const remParam = params['remData'];
      if (remParam) {
        this.rem = JSON.parse(remParam);
        if (this.rem) {
          this.remedialForm.patchValue(this.rem);
        }
      }

      this.passedCustomerId = params['customerId'];
      this.passedCustomerName = params['customerName'];
      if (this.passedCustomerId && this.passedCustomerName) {
        this.selectedCustomer = new Customer();
        this.selectedCustomer.id = +this.passedCustomerId;
        this.selectedCustomer.companyName = this.passedCustomerName;
        this.passedContractNumber = +this.route.snapshot.paramMap.get('contractNumber');
      }
    });

    this.quotes = this.quoteSearchInput.pipe(
      filter(res => res !== null && res.length >= 1),
      map(query => {
        let searchCriteria = new QuoteSearchCriteria();
        searchCriteria.plot = query;
        searchCriteria.customerId = this.selectedCustomer?.id;
        searchCriteria.pageSize = 20;
        searchCriteria.hasContractNumber = true;
        return searchCriteria;
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((query) => this.quoteService.getQuotes(query).pipe()),
      map(x => x.items)
    );

    this.f.traceRequest.patchValue(false);

    // Load customers first, THEN load quote details so this.customers is populated
    this.getCustomers();
  }

  getCustomers() {
    this.customerService.getCustomersByUser()
      .subscribe(customers => {
        this.customers = customers;

        if (this.quoteId != null) {
          this.getQuoteDetails();
        }
      });
  }

  remedialRowsForm = this.formBuilder.group({
    rows: new FormArray([], this.minRowsValidator(1))
  })

  get rows(): FormArray {
    return this.remedialRowsForm.get('rows') as FormArray;
  }

  minRowsValidator(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      return formArray.length < min ? {minRows: {required: min, actual: formArray.length}} : null;
    };
  }


  get rowCheck() {
    if (this.rowItems.length < 1) {
      return true;
    }
    return false;
  }

  addRow() {
    this.rows.push(this.formBuilder.group({
      item: [null, Validators.required],
      issue: [null, Validators.required],
      description: [null],
      mainIssue: [false]
    }));
  }

  removeRow(index: number) {
    this.rows.removeAt(index);
  }


  submitForm() {
    console.log('%c ==>> Validation Errors: ', 'color: red; font-weight: bold; font-size:25px;');

    let totalErrors = 0;

    Object.keys(this.remedialForm.controls).forEach(key => {
      const controlErrors: ValidationErrors = this.remedialForm.get(key).errors;
      if (controlErrors != null) {
        totalErrors++;
        Object.keys(controlErrors).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });

    console.log('Number of errors for rows: ', totalErrors);

    console.log('%c ==>> Validation Errors for rows: ', 'color: red; font-weight: bold; font-size:25px;');

    let totalErrors2 = 0;

    Object.keys(this.remedialRowsForm.controls).forEach(key => {
      const controlErrors2: ValidationErrors = this.remedialRowsForm.get(key).errors;
      if (controlErrors2 != null) {
        totalErrors2++;
        Object.keys(controlErrors2).forEach(keyError => {
          console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors2[keyError]);
        });
      }
    });

    console.log('Number of errors: ', totalErrors2);

    if (!this.remedialForm.valid) {
      this.showAllFormValidation(this.remedialForm);
      return;
    }


    if (!this.remedialRowsForm.valid) {
      this.showAllFormValidation(this.remedialRowsForm);
      return;
    }

    const formValue = this.remedialForm.value;
    const installationDate = formValue.installationDate
      ? new Date(
        formValue.installationDate.year,
        formValue.installationDate.month - 1,
        formValue.installationDate.day
      ).toISOString()
      : null;


    const remedial: any = {
      ...this.remedialForm.value,
      installationDate,
      remedialRows: this.rowItems.length
        ? this.rows.getRawValue().map(r => ({
          item: 'Deck Board',
          issue: r.issue,
          description: r.description ?? '',
          mainIssue: r.mainIssue
        }))
        : [],

      images: this.remedialImages?.webcamImages.map(x => x.imageAsBase64).concat(this.fileContents)
    };

    remedial.location = 'Some Location';

    if (!remedial.contractNumber && !remedial.traceRequest) {
      this.createTraceRequestSwal.fire()
      return;
    }

    remedial.images = this.remedialImages.webcamImages.map(x => x.imageAsBase64).concat(this.fileContents)

    let message = remedial.traceRequest ? 'Trace Request created' : 'Remedial created';

    this.remedialService.createRemedial(remedial)
      .pipe(
        map(r => {
          this.toasterService.success(message);
          if (r.contractNumber) {
            this.router.navigate(['/remedials/' + r.id]);
          } else {
            this.router.navigate(['/remedials']);
          }
        }),
        catchError(e => {
          this.toasterService.error(e);
          return of();
        }))
      .subscribe();
  }

  get f() {
    return this.remedialForm.controls;
  }

  public get f2() {
    return this.remedialRowsForm.controls;
  }

  get serialNumberCtrl(): FormControl {
    return this.remedialForm.get('serialNumber') as FormControl;
  }

  showWebcam() {
    this.showCamera = true;
    this.showDocUpload = false;
  }

  get rowsArray(): FormArray {
    return this.remedialRowsForm.get('rows') as FormArray;
  }

  get rowItems(): FormGroup[] {
    return this.rowsArray.controls as FormGroup[];
  }


  uploadImages() {
    this.showCamera = false;
    this.showDocUpload = true;
  }

  onSelect(event) {
    this.files.push(...event.addedFiles);

    this.fileContents = [];
    this.files.map(x => this.readFile(x).then(result => this.fileContents.push(result)));
  }

  onRemove(event) {
    this.files.splice(this.files.indexOf(event), 1);

    this.fileContents = [];
    this.files.map(x => this.readFile(x).then(result => this.fileContents.push(result)));
  }

  private async readFile(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = e => {
        return resolve((e.target as FileReader).result.toString().split(',')[1]);
      };

      reader.onerror = e => {
        console.error(`FileReader failed on file ${file.name}.`);
        return reject(null);
      };

      if (!file) {
        console.error('No file to read.');
        return reject(null);
      }

      reader.readAsDataURL(file);
    });
  }

  displayQuote(quote: Quote) {
    return quote.plot;
  }

  selectQuote(quote: Quote) {
    if (!quote.contractNumber) {
      this.toasterService.error('Quote must have a contract number to create a remedial');
      return;
    }

    this.f.quoteId.patchValue(quote.id);
    this.f.contractNumber.patchValue(quote.contractNumber);
    this.f.park.patchValue(quote.park);
    this.f.plot.patchValue(quote.plot);
    this.f.installationDate.patchValue(quote.dateFitted);
  }

  selectCustomer($event: any) {
    this.selectedCustomer = $event;
  }

  formatCustomer(customer: Customer) {
    let string = '';
    if (customer.companyName) {
      string += customer.companyName + ' '
    }
    return string;
  }

  sendTraceRequest($event: boolean) {
    if ($event) {
      this.f.traceRequest.setValue(true);
      this.submitForm();
    }
  }

  getAllRemedialItems() {
    this.remedialService.getRemedialItems()
      .pipe(
      )
      .subscribe(items => {
        this.remedialItems = items;
      });
  }

  getAllRemedialLocations() {
    this.remedialService.getRemedialLocations()
      .pipe(
      )
      .subscribe(locations => {
        this.remedialLocations = locations;
      });
  }

  setMainIssue(index: number) {
    this.rows.controls.forEach((group, i) => {
      group.get('mainIssue')?.setValue(i === index);
    });
  }

  isInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  hasError(control: AbstractControl | null, error: string): boolean {
    return !!control && !!control.errors?.[error];
  }

  get descriptionCtrl(): AbstractControl | null {
    return this.remedialForm.get('description');
  }

  isInteracted(control: AbstractControl | null): boolean {
    return !!control && (control.dirty || control.touched);
  }

  onDateSelect(date: NgbDateStruct) {
    this.installationDateStruct = date;
    const instDate = new Date(date.year, date.month - 1, date.day);
    this.remedialForm.get('installationDate')?.setValue(instDate);
  }

  getQuoteDetails() {
    this.quoteService.getQuote(this.quoteId)
      .subscribe(quote => {
        this.f.quoteId.patchValue(quote.id);
        this.f.contractNumber.patchValue(quote.contractNumber);
        this.f.park.patchValue(quote.park);
        this.f.plot.patchValue(quote.plot);
        this.f.installationDate.patchValue(quote.dateFitted);

        if (quote.customer.id) {
          this.selectedCustomer = this.customers.find(c => c.id === quote.customer.id) ?? null;
        }

        this.selectedQuote = quote;
      })
  }

  compareQuotes(a: Quote, b: Quote): boolean {
    return a?.id === b?.id;
  }

  compareCustomers(a: Customer, b: Customer): boolean {
    return a?.id === b?.id;
  }
}
